import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Users, MapPin, Repeat, Palmtree } from 'lucide-react';
import { courseTypes } from './constants';

// Memoized component for rendering week separator
export const WeekSeparator = React.memo(({ weekStart, weekEnd, vacancePeriod, isInVacation }) => (
  <div className={`flex items-center gap-3 py-2 ${!isInVacation && !vacancePeriod ? 'mt-4' : ''}`}>
    <div className={`h-px flex-1 ${vacancePeriod || isInVacation ? 'bg-red-300' : 'bg-stone-300'}`} />
    <span className={`text-sm font-medium whitespace-nowrap flex items-center gap-1 ${vacancePeriod || isInVacation ? 'text-red-700' : 'text-stone-600'}`}>
      Semaine du {format(weekStart, 'd MMM', { locale: fr })} au {format(weekEnd, 'd MMM yyyy', { locale: fr })}
      {vacancePeriod && (
        <span className="inline-flex items-center gap-1 ml-2">
          <Palmtree className="w-4 h-4" />
          {vacancePeriod.nom}
        </span>
      )}
    </span>
    <div className={`h-px flex-1 ${vacancePeriod || isInVacation ? 'bg-red-300' : 'bg-stone-300'}`} />
  </div>
));

WeekSeparator.displayName = 'WeekSeparator';

// Memoized component for rendering a single course card in list view
export const CourseListCard = React.memo(({ 
  course, 
  coursDate, 
  isPast, 
  isScrollTarget, 
  isNewlyCreated,
  todayRef,
  newCoursRef,
  getThemeColor, 
  getCoursTitle, 
  getInstructeurName, 
  getGroupeName,
  getInscritsForCours,
  handleSelectCours
}) => {
  const inscrits = getInscritsForCours(course);
  const inscritsPresents = inscrits.filter(i => !i.absent);
  const placesRestantes = course.places_max - inscritsPresents.length;
  
  return (
    <Card 
      ref={isNewlyCreated ? newCoursRef : (isScrollTarget ? todayRef : null)}
      className={`card-hover cursor-pointer transition-all overflow-hidden ${isPast ? 'opacity-75 bg-stone-50' : ''}`}
      onClick={() => handleSelectCours(course)}
      data-testid={`cours-list-${course.id}`}
      style={{ touchAction: 'pan-y' }}
    >
      <CardContent className="p-0">
        <div className="flex items-stretch">
          {/* Date column with theme color background */}
          <div 
            className="flex flex-col items-center justify-center min-w-[70px] py-3 px-2"
            style={{ backgroundColor: getThemeColor(course.theme_id) + '15' }}
          >
            <p className={`text-xs uppercase font-medium ${isPast ? 'text-stone-400' : ''}`}
               style={{ color: isPast ? undefined : getThemeColor(course.theme_id) }}>
              {format(coursDate, 'EEE', { locale: fr })}
            </p>
            <p className={`text-2xl font-bold ${isPast ? 'text-stone-400' : ''}`}
               style={{ color: isPast ? undefined : getThemeColor(course.theme_id) }}>
              {format(coursDate, 'd')}
            </p>
            <p className={`text-xs ${isPast ? 'text-stone-400' : ''}`}
               style={{ color: isPast ? undefined : getThemeColor(course.theme_id) + 'CC' }}>
              {format(coursDate, 'MMM', { locale: fr })}
            </p>
          </div>
          
          {/* Course info */}
          <div className="flex-1 p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold ${isPast ? 'text-stone-500' : 'text-stone-800'}`}>
                {getCoursTitle(course)}
              </h3>
              {course.niveau_galop && (
                <Badge variant="secondary" className={`text-xs ${isPast ? 'opacity-60' : ''}`}>
                  G{course.niveau_galop}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${isPast ? 'bg-stone-100 text-stone-500' : 'bg-stone-100 text-stone-700'}`}>
                {course.type_activite || courseTypes.find(t => t.value === course.type_cours)?.label || 'Cours'}
                {course.recurrence === 'hebdomadaire' && (
                  <Repeat className="w-3 h-3 text-purple-500" />
                )}
                {getInstructeurName(course.instructeur_id) && (
                  <span className="text-stone-500 text-[11px] ml-1">
                    • {getInstructeurName(course.instructeur_id).split(' ')[0]}
                  </span>
                )}
              </span>
              {course.groupe_fixe_id && (
                <Badge className={`text-xs bg-purple-100 text-purple-700 ${isPast ? 'opacity-60' : ''}`}>
                  {getGroupeName(course.groupe_fixe_id)}
                </Badge>
              )}
              {isPast && (
                <Badge className="text-xs bg-stone-200 text-stone-500">Passé</Badge>
              )}
            </div>
          
            <div className={`flex items-center gap-3 mt-2 text-xs ${isPast ? 'text-stone-400' : 'text-stone-500'}`}>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {format(coursDate, 'HH:mm')} • {course.duree_minutes}min
              </span>
              <span className={`flex items-center gap-1 font-medium ${
                placesRestantes === 0 
                  ? 'text-rose-500' 
                  : isPast 
                    ? 'text-stone-400' 
                    : 'text-emerald-600'
              }`}>
                <Users className="w-3.5 h-3.5" />
                {placesRestantes === 0 ? 'Complet' : `${placesRestantes} place${placesRestantes > 1 ? 's' : ''}`}
              </span>
              {course.lieu && (
                <span className="flex items-center gap-1 hidden sm:flex">
                  <MapPin className="w-3.5 h-3.5" />
                  {course.lieu}
                </span>
              )}
            </div>
            
            {inscrits.length > 0 && (
              <div className={`flex flex-wrap items-center gap-1 mt-2 text-xs ${isPast ? 'opacity-70' : ''}`}>
                {inscrits.map((inscrit, idx) => (
                  <span 
                    key={inscrit.cavalier_id || idx}
                    className={`px-1.5 py-0.5 rounded ${
                      inscrit.absent
                        ? 'bg-rose-100 text-rose-500 line-through'
                        : inscrit.type === 'ponctuel'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-[#2E4A3D]/10 text-[#2E4A3D]'
                    }`}
                  >
                    {inscrit.pseudo || `${inscrit.prenom?.[0]}${inscrit.nom?.[0]}`}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

CourseListCard.displayName = 'CourseListCard';

// Main list view renderer - renders grouped course data
export const ListViewRenderer = React.memo(({ 
  groupedData, 
  todayRef, 
  newCoursRef,
  getThemeColor, 
  getCoursTitle, 
  getInstructeurName, 
  getGroupeName,
  getInscritsForCours,
  handleSelectCours
}) => {
  return (
    <div className="overflow-x-hidden" style={{ touchAction: 'pan-y' }}>
      {groupedData.map((item) => {
        if (item.type === 'week-separator') {
          return (
            <WeekSeparator 
              key={item.key}
              weekStart={item.weekStart}
              weekEnd={item.weekEnd}
              vacancePeriod={item.vacancePeriod}
              isInVacation={item.isInVacation}
            />
          );
        }
        
        if (item.type === 'vacation-group') {
          return (
            <div key={item.key} className="bg-red-100/60 border-l-4 border-red-400 -mx-4 px-4 py-3 space-y-3 my-3 rounded-r-lg">
              {item.items.map((vacItem) => {
                if (vacItem.type === 'week-separator') {
                  return (
                    <WeekSeparator 
                      key={vacItem.key}
                      weekStart={vacItem.weekStart}
                      weekEnd={vacItem.weekEnd}
                      vacancePeriod={vacItem.vacancePeriod}
                      isInVacation={true}
                    />
                  );
                }
                return (
                  <CourseListCard
                    key={vacItem.course.id}
                    course={vacItem.course}
                    coursDate={vacItem.coursDate}
                    isPast={vacItem.isPast}
                    isScrollTarget={vacItem.isScrollTarget}
                    isNewlyCreated={vacItem.isNewlyCreated}
                    todayRef={todayRef}
                    newCoursRef={newCoursRef}
                    getThemeColor={getThemeColor}
                    getCoursTitle={getCoursTitle}
                    getInstructeurName={getInstructeurName}
                    getGroupeName={getGroupeName}
                    getInscritsForCours={getInscritsForCours}
                    handleSelectCours={handleSelectCours}
                  />
                );
              })}
            </div>
          );
        }
        
        if (item.type === 'course') {
          return (
            <div key={item.course.id} className="mt-3">
              <CourseListCard
                course={item.course}
                coursDate={item.coursDate}
                isPast={item.isPast}
                isScrollTarget={item.isScrollTarget}
                isNewlyCreated={item.isNewlyCreated}
                todayRef={todayRef}
                newCoursRef={newCoursRef}
                getThemeColor={getThemeColor}
                getCoursTitle={getCoursTitle}
                getInstructeurName={getInstructeurName}
                getGroupeName={getGroupeName}
                getInscritsForCours={getInscritsForCours}
                handleSelectCours={handleSelectCours}
              />
            </div>
          );
        }
        
        return null;
      })}
    </div>
  );
});

ListViewRenderer.displayName = 'ListViewRenderer';
